import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useQueryMutation } from '@lore/query-connect';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import CloseDialogButton from '@/dialogs_common/CloseDialogButton';
import ActionButton from '@/components/ui/ActionButton';
import { LinkButton } from '@communityshare/ui-shared';
import Overlay from '@/forms/_common/Overlay';
import useProjectChannel from '@/hooks/useProjectChannel';

const SUCCESS_CLOSE_MS = 1500;

ProjectTitleEditDialog.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    data: PropTypes.shape({
      title: PropTypes.string
    }).isRequired
  }).isRequired,
  onCancel: PropTypes.func.isRequired
};

export default function ProjectTitleEditDialog(props) {
  const { project, onCancel } = props;
  const { t } = useTranslation();

  const [title, setTitle] = useState(project.data.title ?? '');
  const textareaRef = useRef(null);

  const mutation = useQueryMutation('project.update', project);
  const { saving, success, error } = mutation;

  const broadcast = useProjectChannel(project.id);

  // Deferred so it runs after the dialog wrapper's focus trap, which would
  // otherwise claim focus in a later parent effect.
  useEffect(() => {
    const timer = setTimeout(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (success) {
      // title holds exactly the value that was just saved
      broadcast({ title });
      const timer = setTimeout(() => onCancel(), SUCCESS_CLOSE_MS);
      return () => clearTimeout(timer);
    }
  }, [success, onCancel, broadcast, title]);

  const hasChanges = title !== project.data.title;
  const isEmpty = title.trim().length === 0;

  function onSave() {
    mutation.mutate({ title });
  }

  return (
    <DialogTemplate className="w-screen max-w-screen-sm">
      <CloseDialogButton onCancel={onCancel} />
      <div className="px-6 pt-6 pb-8 sm:px-8">
        <Overlay isVisible={saving}>
          <LinkButton
            label={t('projects.edit_guiding_question.back')}
            icon="left"
            onClick={onCancel}
          />
          <h2 className="mt-2 text-3xl font-semibold text-cs-gray-900">
            {t('projects.edit_guiding_question.title')}
          </h2>
          <p className="mt-2 text-base text-cs-gray-500">
            {t('projects.edit_guiding_question.subtitle')}
          </p>
          <label
            htmlFor="guiding-question"
            className="mt-6 block text-base text-cs-gray-900"
          >
            {t('projects.edit_guiding_question.field_label')}
          </label>
          <div className="mt-1 bg-white border border-cs-gray-300 rounded-cs-10 overflow-hidden focus-within:border-cs-blue-500 focus-within:ring-1 focus-within:ring-cs-blue-500">
            <textarea
              id="guiding-question"
              ref={textareaRef}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              rows={4}
              disabled={saving}
              className="w-full p-4 text-base text-cs-gray-900 leading-6 resize-none border-0 outline-none focus:ring-0"
            />
            <div className="border-t border-cs-gray-200 px-4 py-2">
              <LinkButton
                label={t('projects.edit_guiding_question.reset')}
                icon="leading"
                leadingIcon={ArrowPathIcon}
                disabled={!hasChanges || saving}
                onClick={() => setTitle(project.data.title)}
              />
            </div>
          </div>
          <p className="mt-2 text-sm text-cs-gray-500">
            {t('projects.edit_guiding_question.help')}
          </p>
          {success && (
            <div
              role="status"
              className="mt-6 bg-green-50 border border-cs-green-500 rounded-cs-10 p-4 flex items-start gap-2 overflow-hidden animate-grow-in"
            >
              <CheckCircleIcon className="h-6 w-6 text-cs-green-500 flex-shrink-0" aria-hidden="true" />
              <p className="text-base font-semibold text-cs-gray-900">
                {t('projects.edit_guiding_question.success')}
              </p>
            </div>
          )}
          {error && (
            <div
              role="alert"
              className="mt-6 bg-red-50 border border-red-500 rounded-cs-10 p-4 flex items-start gap-2"
            >
              <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0" aria-hidden="true" />
              <div className="flex flex-col gap-1">
                <p className="text-base font-semibold text-cs-gray-900">
                  {t('projects.edit_guiding_question.error_title')}
                </p>
                <p className="text-base text-cs-gray-900">
                  {t('projects.edit_guiding_question.error_body')}
                </p>
              </div>
            </div>
          )}
          <div className="mt-6 flex justify-between items-center gap-3">
            <ActionButton variant="secondary" onClick={onCancel} disabled={saving}>
              {t('projects.edit_guiding_question.cancel')}
            </ActionButton>
            <ActionButton
              variant="primary"
              loading={saving}
              loadingLabel={t('projects.edit_guiding_question.saving')}
              disabled={isEmpty || !hasChanges}
              onClick={onSave}
            >
              {t('projects.edit_guiding_question.save')}
            </ActionButton>
          </div>
        </Overlay>
      </div>
    </DialogTemplate>
  );
}
